import { makeStyles, mergeClasses } from "@fluentui/react-components";
import {
  AuthenticatedTemplate,
  QueryClientProvider,
  UnauthenticatedTemplate,
  useMsalAuthRedirect,
} from "@skanska/vsa-client-auth";
import { QueryClient } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import ErrorFallbackApp from "./components/fallback/ErrorFallbackApp";
import Header from "./components/header/Header";
import { loginRequest } from "./config/MsalConfig";
import { reactQueryClientConfig } from "./config/ReactQueryConfig";
import "./localization/i18n";
import { ClientRoutes } from "./models/ClientRoutes";
import ActivitiesPage from "./pages/ActivitiesPage";
import ActivityOverviewPage from "./pages/ActivityOverviewPage";
import CacheManagementPage from "./pages/CacheManagementPage";
import ChaptersPage from "./pages/ChaptersPage";
import CreateActivity from "./pages/CreateActivity";
import CreateChapterPage from "./pages/CreateChapterPage";
import CreateImagePage from "./pages/CreateImagePage";
import CreateToolPage from "./pages/CreateToolPage";
import DocumentManagementPage from "./pages/DocumentManagementPage";
import DocumentToolsPage from "./pages/DocumentToolsPage";
import EditActivity from "./pages/EditActivity";
import EditChapterPage from "./pages/EditChapterPage";
import EditImagePage from "./pages/EditImagePage";
import EditToolPage from "./pages/EditToolPage";
import ImageOverviewPage from "./pages/ImageOverviewPage";
import LinkManagementPage from "./pages/LinkManagementPage";
import LinksToolsPage from "./pages/LinksToolsPage";
import MediaPage from "./pages/MediaPage";
import PageNotFound from "./pages/PageNotFound";
import ReindexJobMonitoringPage from "./pages/ReindexJobMonitoringPage";
import RoleAssignmentPage from "./pages/RoleAssignmentPage";
import SearchSettingsPage from "./pages/SearchSettingsPage";
import StartPage from "./pages/StartPage";
import SystemAdminPage from "./pages/SystemAdminPage";
import TagManagerPage from "./pages/TagManagerPage";
import ToolOverviewPage from "./pages/ToolOverviewPage";
import UtilityLinksAdminPage from "./pages/UtilityLinksAdminPage";
import SystemInfoBanner from "./components/systemInfoBanner/SystemInfoBanner";
import SystemInfoBannerAdminPage from "./pages/SystemInfoBannerAdminPage";
import PublishedDocumentMetadataMigrationPage from "./pages/PublishedDocumentMetadataMigrationPage";
import PublishedMediaMetadataMigrationPage from "./pages/PublishedMediaMetadataMigrationPage";
import ImageMetadataUpdaterPage from "./pages/ImageMetadataUpdaterPage";
import { SystemAdminRouteGuard } from "./components/routeguards/SystemAdminRouteGuard";
import EntraGroupMembershipPage from "./pages/EntraGroupMembershipPage";
import { SuperAdminRouteGuard } from "./components/routeguards/SuperAdminRouteGuard";
import ReportsPage from "./pages/ReportsPage";
import { AdminRouteGuard } from "./components/routeguards/AdminRouteGuard";
import { ImprovementLeaderExtRouteGuard } from "./components/routeguards/ImprovementLeaderExtRouteGuard";
import ImageFileMigrationPage from "./pages/ImageFileMigrationPage";

const useStyles = makeStyles({
  content: {
    padding: "5px",
    borderRadius: "8px",
    display: "flex",
    justifyContent: "center",
    margin: "auto",
    height: "100vh",
    alignItems: "center",
  },
  centeredText: {
    textAlign: "center",
  },
  layoutContent: {
    width: "100%",
    maxWidth: "1900px",
    margin: "0 auto",
  },
  fullWidthLayoutContent: {
    maxWidth: "none",
    margin: 0,
  },
});

const fullWidthLayoutPaths = new Set<string>([
  ClientRoutes.Documents,
  ClientRoutes.Activities,
  ClientRoutes.Media,
  ClientRoutes.Links,
]);

function Layout() {
  const classes = useStyles();
  const location = useLocation();
  const usesFullWidthLayout = fullWidthLayoutPaths.has(location.pathname);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallbackApp}>
      <Header />
      <SystemInfoBanner />
      <main
        className={mergeClasses(
          classes.layoutContent,
          usesFullWidthLayout && classes.fullWidthLayoutContent
        )}
      >
        <Outlet />
      </main>
    </ErrorBoundary>
  );
}

function App() {
  useMsalAuthRedirect(loginRequest);
  const classes = useStyles();
  const { t } = useTranslation();

  const queryClient = new QueryClient(reactQueryClientConfig);

  const router = createBrowserRouter([
    {
      element: <Layout />,
      children: [
        {
          path: "/",
          element: <StartPage />,
        },
        {
          path: "*",
          element: <PageNotFound />,
        },
        {
          path: ClientRoutes.ActivitiesPublished + "/:id",
          element: <ActivityOverviewPage />,
        },
        {
          path: ClientRoutes.ToolsDrafts,
          element: <Navigate to={`/`} />,
        },
        {
          path: ClientRoutes.ToolsPublished,
          element: <Navigate to={`/`} />,
        },
        {
          path: ClientRoutes.ToolsPublished + "/:id",
          element: <ToolOverviewPage oldToolVersion={false} />,
        },
        {
          path: ClientRoutes.ToolsPublishedOld + "/:id",
          element: <ToolOverviewPage oldToolVersion={true} />,
        },
        {
          element: <ImprovementLeaderExtRouteGuard />,
          children: [
            {
              path: ClientRoutes.ActivityCreate + "/",
              element: <CreateActivity />,
            },
            {
              path: ClientRoutes.Activities + "/:id",
              element: <EditActivity />,
            },
            {
              path: ClientRoutes.ToolCreate,
              element: <CreateToolPage />,
            },
            {
              path: `${ClientRoutes.ToolCreate}?toolType=Document`,
              element: <CreateToolPage />,
            },
            {
              path: ClientRoutes.ToolsDrafts + "/:id",
              element: <EditToolPage />,
            },
            {
              path: ClientRoutes.Reports,
              element: <ReportsPage />,
            },
          ],
        },
        {
          element: <AdminRouteGuard />,
          children: [
            {
              path: ClientRoutes.Documents,
              element: <DocumentToolsPage />,
            },
            {
              path: ClientRoutes.Chapters,
              element: <ChaptersPage />,
            },
            {
              path: ClientRoutes.Chapters + "/:id",
              element: <EditChapterPage />,
            },
            {
              path: ClientRoutes.CreateChapter,
              element: <CreateChapterPage />,
            },
            {
              path: ClientRoutes.TagManager,
              element: <TagManagerPage />,
            },
            {
              path: ClientRoutes.Activities,
              element: <ActivitiesPage />,
            },
            {
              path: ClientRoutes.ActivitiesPublished,
              element: (
                <Navigate to={`${ClientRoutes.Activities}?view=published`} />
              ),
            },
            {
              path: ClientRoutes.Media,
              element: <MediaPage />,
            },
            {
              path: ClientRoutes.MediaActivityCreate,
              element: <CreateImagePage />,
            },
            {
              path: ClientRoutes.MediaActivityEdit + "/:id",
              element: <EditImagePage />,
            },
            {
              path: ClientRoutes.MediaActivityDraft + "/:id",
              element: <EditImagePage />,
            },
            {
              path: ClientRoutes.MediaActivityEdit + "/overview/:id",
              element: <ImageOverviewPage />,
            },
            {
              path: ClientRoutes.SystemInfoBanner,
              element: <SystemInfoBannerAdminPage />,
            },
            {
              path: ClientRoutes.Links,
              element: <LinksToolsPage />,
            },
            {
              path: `${ClientRoutes.ToolCreate}?toolType=Link`,
              element: <CreateToolPage />,
            },
            {
              path: ClientRoutes.Roles,
              element: <RoleAssignmentPage />,
            },
          ],
        },
        {
          element: <SuperAdminRouteGuard />,
          children: [
            {
              path: ClientRoutes.EntraGroupMemberships,
              element: <EntraGroupMembershipPage />,
            },
            {
              path: ClientRoutes.MediaChapterCreate,
              element: <CreateImagePage />,
            },
            {
              path: ClientRoutes.MediaChapterEdit + "/:id",
              element: <EditImagePage />,
            },
          ],
        },
        {
          element: <SystemAdminRouteGuard />,
          children: [
            {
              path: ClientRoutes.PublishedDocumentMetadataMigration,
              element: <PublishedDocumentMetadataMigrationPage />,
            },
            {
              path: ClientRoutes.PublishedMediaMetadataMigration,
              element: <PublishedMediaMetadataMigrationPage />,
            },
            {
              path: ClientRoutes.ImageFileMigration,
              element: <ImageFileMigrationPage />,
            },
            {
              path: ClientRoutes.ImageMetadataUpdater,
              element: <ImageMetadataUpdaterPage />,
            },
            {
              path: ClientRoutes.SystemAdmin,
              element: <SystemAdminPage />,
            },
            {
              path: ClientRoutes.SystemAdminCache,
              element: <CacheManagementPage />,
            },
            {
              path: ClientRoutes.SystemAdminSearchSettings,
              element: <SearchSettingsPage />,
            },
            {
              path: ClientRoutes.SystemAdminDocumentManagement,
              element: <DocumentManagementPage />,
            },
            {
              path: ClientRoutes.SystemAdminLinkManagement,
              element: <LinkManagementPage />,
            },
            {
              path: ClientRoutes.ReindexJobMonitoring,
              element: <ReindexJobMonitoringPage />,
            },
            {
              path: ClientRoutes.SystemAdminUtilityLinks,
              element: <UtilityLinksAdminPage />,
            },
          ],
        },
      ],
    },
  ]);
  return (
    <>
      <AuthenticatedTemplate>
        <QueryClientProvider queryClient={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <div className={classes.content}>
          <h4 className={classes.centeredText}>{t("authenticating")}</h4>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

export default App;
